// The design page inside the full-screen layer of the store's product page ("embed mode").
//
// The store snippet (shopify/rg-personalise.liquid) opens this page in a frame, with the same link plus
//   embed=1&parent=<the address of the store page>
// The two talk with messages (window.postMessage). Every message is checked: the window it came from, its origin and its shape.
// Nothing is ever posted with the target "*": this page posts to the parent's address only.
//
//   this page -> store   { type: "rg:ready" }                              the page is loaded and listening
//                        { type: "rg:close" }                              the customer left
//                        { type: "rg:done", design, variant, extra }       a design is saved: the values the return link carries
//                                                                          (rg_design, rg_variant, rg_extra); the store puts it in the cart
//                        { type: "rg:fetch-products", handles: [...] }     at most 12 handles
//   store -> this page   { type: "rg:product", product }                   the store's /products/<handle>.js (null: the store page could not read it)
//                        { type: "rg:products", products: { <handle>: json or null } }   the answer to rg:fetch-products
//
// Embed mode starts only when embed=1, the page is in a frame, and parent= is an allowed address (the same rule as return=:
// a .myshopify.com store, rightgifting.com, www.rightgifting.com or a local test address). In any other case nothing changes.
// What the store sends is only used for the live sizes and prices (store-sizes.js). If it does not answer within a few seconds,
// the page asks the shop itself, as it always did.

// The same rule as cleanReturn() in products.js (copied on purpose, so this file stands on its own). tests/embed.test.mjs checks
// that the two always agree.
const RETURN_HOSTS = ["rightgifting.com", "www.rightgifting.com"];
export function allowedOrigin(raw) {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    const shopSite = u.protocol === "https:" && (u.hostname.endsWith(".myshopify.com") || RETURN_HOSTS.includes(u.hostname));
    const localTest = u.protocol === "http:" && (u.hostname === "127.0.0.1" || u.hostname === "localhost");
    return shopSite || localTest ? u.origin : "";
  } catch (e) { return ""; }
}

export const HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/;
export const MAX_HANDLES = 12;
const MAX_VARIANTS = 2048;                       // the most variants a Shopify product can have
const VARIANT_RE = /^\d{1,20}$/;
const GIFT_ID_RE = /^[A-Za-z0-9_-]{22}$/;        // the id of a design: 22 URL-safe characters
export const ASK_WAIT_MS = 3000;                 // how long the page waits for the store before it asks the shop itself

const plain = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const isProduct = (v) => plain(v) && Array.isArray(v.variants) && v.variants.length <= MAX_VARIANTS;

// { product } (product: the store's answer, or null) for a good rg:product message, else null.
export function readProductMessage(data) {
  if (!plain(data) || data.type !== "rg:product") return null;
  if (data.product === null) return { product: null };
  return isProduct(data.product) ? { product: data.product } : null;
}

// Map(handle -> the store's answer or null) for a good rg:products message, else null. One wrong entry makes the whole message wrong.
export function readProductsMessage(data) {
  if (!plain(data) || data.type !== "rg:products" || !plain(data.products)) return null;
  const keys = Object.keys(data.products);
  if (!keys.length || keys.length > MAX_HANDLES) return null;
  const out = new Map();
  for (const h of keys) {
    const v = data.products[h];
    if (!HANDLE_RE.test(h) || (v !== null && !isProduct(v))) return null;
    out.set(h, v);
  }
  return out;
}

// The rg:done message: the values of the return link (rg_design, rg_variant, rg_extra). variant is digits or "".
export function doneMessage({ design, variant = "", extra = [] }) {
  return {
    type: "rg:done",
    design: String(design),
    variant: VARIANT_RE.test(String(variant || "")) ? String(variant) : "",
    extra: (Array.isArray(extra) ? extra : []).filter((id) => typeof id === "string" && GIFT_ID_RE.test(id))
  };
}

// Starts embed mode. Returns null when the page is not embedded (or the parent is not an allowed address).
//   handle: the product of this page (the answer of rg:product is for it).
// The result: { origin, ready(), close(), done({ design, variant, extra }), get(handle), stop() }
//   get(handle) asks the store page for /products/<handle>.js. It gives the answer, or null (no answer, or the store could not read it).
export function startEmbed({ win = window, handle = "", waitMs = ASK_WAIT_MS } = {}) {
  let q;
  try { q = new URLSearchParams(win.location.search); } catch (e) { return null; }
  if (q.get("embed") !== "1" || q.get("adjust") === "1") return null;     // the Adjust frame of the gifts step is never embedded
  const parent = win.parent;
  if (!parent || parent === win) return null;                              // not in a frame
  const origin = allowedOrigin((q.get("parent") || "").trim());
  if (!origin) return null;

  const post = (msg) => { try { parent.postMessage(msg, origin); } catch (e) { /* the page around us is gone */ } };
  const mine = HANDLE_RE.test(String(handle)) ? String(handle) : "";       // rg:product is the product of this page
  const answers = new Map();                                               // handle -> the store's answer (an object, or null)
  const pending = new Map();                                               // handle -> { promise, resolve, timer }
  const asked = new Set();                                                 // the handles asked with rg:fetch-products: only these are taken from rg:products
  let queue = [], flushTimer = null;

  function settle(h, value) {
    answers.set(h, value);
    const p = pending.get(h);
    if (p) { clearTimeout(p.timer); pending.delete(h); p.resolve(value); }
  }
  function flush() {
    flushTimer = null;
    const handles = queue; queue = [];
    for (let i = 0; i < handles.length; i += MAX_HANDLES) post({ type: "rg:fetch-products", handles: handles.slice(i, i + MAX_HANDLES) });
  }
  function get(h) {
    h = String(h || "");
    if (!HANDLE_RE.test(h)) return Promise.resolve(null);
    if (answers.has(h)) return Promise.resolve(answers.get(h));
    const known = pending.get(h);
    if (known) return known.promise;
    const p = {};
    p.promise = new Promise((resolve) => { p.resolve = resolve; });
    p.timer = setTimeout(() => { pending.delete(h); p.resolve(null); }, waitMs);
    pending.set(h, p);
    if (h !== mine) {                                                      // the page's own product comes by itself, after rg:ready
      asked.add(h); queue.push(h);
      if (!flushTimer) flushTimer = setTimeout(flush, 0);                  // handles asked together go in one message
    }
    return p.promise;
  }

  function onMessage(e) {
    if (e.source !== parent || e.origin !== origin) return;                // only the page around us, from its own address
    const one = readProductMessage(e.data);
    if (one) {
      if (!mine) return;
      const named = one.product && typeof one.product.handle === "string" ? one.product.handle.toLowerCase() : mine;
      if (named === mine) settle(mine, one.product);                       // an answer for another product is not for this page
      return;
    }
    const many = readProductsMessage(e.data);
    if (many) for (const [h, v] of many) if (asked.has(h)) settle(h, v);
  }
  win.addEventListener("message", onMessage);

  return {
    origin,
    ready: () => post({ type: "rg:ready" }),
    close: () => post({ type: "rg:close" }),
    done: (d) => post(doneMessage(d)),
    get,
    stop: () => { win.removeEventListener("message", onMessage); clearTimeout(flushTimer); for (const p of pending.values()) clearTimeout(p.timer); pending.clear(); }
  };
}
